import { useState } from "react";
import { noop } from "lodash";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { mapParticipantsPerStatus } from "@/react/components/Chat/MessagingV2/RailBar/helpers";
import { BlockDmsModal } from "@/react/components/Chat/sharedV2/BlockDmsModal";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { DIRECT_CHAT_ROOM } from "@/react/components/constants";
import { getRouteWithPrevLocation } from "@/react/helpers/urlHelpers";
import { useChatRoom } from "@/react/hooks/chatsV2";
import { useBlockDms } from "@/react/hooks/chatsV2/blockDms/useBlockDms";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import { usePunditUserContext } from "@circle-react/contexts";
import { reactQueryDelete } from "@circle-react/helpers/backendRequestHelpers";
import { chatRoomPath, internalApi } from "@circle-react/helpers/urlHelpers";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { useToast } from "@circle-react-uikit/ToastV2";
import { ParticipantList } from "./ParticipantList";
import { useMessageActions } from "./hooks/useMessageActions";
import { useCanMessageMember } from "@/react/components/Profile/useCanMessageMember";

interface ContentProps {
  fetchNextPage: () => void;
  hasMoreSpaceMembers?: boolean;
  refetchSpaceMembers: () => void;
  space: any;
  spaceMembers: any[];
}

interface ProfileActionItem {
  className?: string;
  color?: string;
  icon: string;
  iconClassName?: string;
  iconSize?: number;
  label: string;
  onClick: (() => void) | (() => Promise<void>);
  value: string;
}

export const Content = ({
  spaceMembers,
  space,
  refetchSpaceMembers,
  hasMoreSpaceMembers = false,
  fetchNextPage,
}: ContentProps) => {
  const toastr = useToast();
  const history = useHistory();
  const { findOrCreateChatRoom } = useChatRoom();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isBlockDmsModalOpen, setIsBlockDmsModalOpen] = useState(false);
  const [memberToBlock, setMemberToBlock] = useState<any>(null);
  const [memberToDelete, setMemberToDelete] = useState<any>(null);
  const memberProfileDrawer = useProfileDrawer();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const { shouldShowRemoveOption } = useMessageActions();
  const { shouldShowMessageOption } = useCanMessageMember();
  const { canBlockMemberDm } = useBlockDms();

  const localeScope = "members_directory.community_members.actions.space";

  const { mutate: leaveSpaceMutation, isLoading: isLeaving } = useMutation(
    async () => {
      await reactQueryDelete(
        internalApi.spaceMembers.leaveSpace({
          spaceId: space.id,
          memberId: memberToDelete?.community_member_id,
        }),
      );
    },
    {
      onError: (error: Error) => {
        toastr.error(error.message);
      },

      onSuccess: () => {
        toastr.success(t(`${localeScope}.successfully_removed`));
        refetchSpaceMembers();
      },

      onSettled: () => {
        setIsConfirmationModalOpen(false);
      },
    },
  );

  const toggleBlockDmsModal = (member: any) => {
    setMemberToBlock(member);
    setIsBlockDmsModalOpen(true);
  };

  const getProfileActionItems = (member: any) => {
    const handleDirectMessaging = async () => {
      const chatRoomUuid = await findOrCreateChatRoom({
        kind: DIRECT_CHAT_ROOM,
        community_member_ids: [member?.community_member_id],
      });
      if (chatRoomUuid) {
        history.push(
          getRouteWithPrevLocation({
            url: chatRoomPath({ uuid: chatRoomUuid }),
          }),
        );
      }
    };

    const profileActionItems: ProfileActionItem[] = [
      {
        value: "profile",
        icon: "20-eye-open",
        iconSize: 20,
        className: "!py-2.5 !px-3",
        label: t("view_profile"),
        onClick: () =>
          memberProfileDrawer.show({ memberPublicId: member?.user_public_uid }),
      },
    ];

    if (shouldShowMessageOption(member)) {
      profileActionItems.push({
        value: "message",
        icon: "20-dm-message",
        className: "!py-2.5 !px-3",
        iconSize: 20,
        label: t("messaging.send_direct_message"),
        onClick: isViewOnlyMasquerading ? noop : handleDirectMessaging,
      });
    }
    if (canBlockMemberDm(member)) {
      profileActionItems.push({
        value: "block-dm",
        icon: "20-block-dms",
        className: "!py-2.5 !px-3",
        iconSize: 20,
        label: t("messaging.block_direct_messages"),
        iconClassName: "text-feedback-error",
        color: "text-feedback-error",
        onClick: () => toggleBlockDmsModal(member),
      });
    }
    if (shouldShowRemoveOption(member)) {
      profileActionItems.push({
        value: "remove",
        color: "text-feedback-error",
        icon: "20-close",
        iconSize: 20,
        className: "!py-2.5 !px-3",
        iconClassName: "text-feedback-error",
        label: t("remove"),
        onClick: () => {
          setMemberToDelete(member);
          setIsConfirmationModalOpen(true);
        },
      });
    }

    return profileActionItems;
  };

  const { onlineParticipants, offlineParticipants } =
    mapParticipantsPerStatus(spaceMembers);

  const handleBlockDms = () => {
    setIsBlockDmsModalOpen(false);
    toastr.success(
      t("messaging.block_direct_messages_modal.success", {
        name: memberToBlock?.name,
      }),
    );
  };

  return (
    <>
      <div
        id="scrollableDiv"
        className="p-0 md:pb-4"
        style={{ height: "100%", overflow: "auto" }}
      >
        <InfiniteScroll
          scrollThreshold={0.8}
          next={fetchNextPage}
          hasMore={hasMoreSpaceMembers}
          dataLength={spaceMembers.length}
          className="p-0 px-6 pb-12"
          scrollableTarget="scrollableDiv"
          loader={
            <div className="flex justify-center">
              <Typography.LabelXxs>
                {t("toggle_with_loader.loading")}
              </Typography.LabelXxs>
            </div>
          }
        >
          {onlineParticipants.length > 0 && (
            <>
              <div className="text-default mb-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.online")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                participantList={onlineParticipants}
                isViewOnlyMasquerading={isViewOnlyMasquerading}
                getProfileActionItems={getProfileActionItems}
              />
            </>
          )}
          {offlineParticipants.length > 0 && (
            <>
              <div className="text-default my-2">
                <Typography.LabelXxs weight="bold" color="text-default">
                  {t("chat_space.offline")}
                </Typography.LabelXxs>
              </div>
              <ParticipantList
                participantList={offlineParticipants}
                isViewOnlyMasquerading={isViewOnlyMasquerading}
                getProfileActionItems={getProfileActionItems}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title={t("remove")}
        onClose={() => setIsConfirmationModalOpen(false)}
        onCancel={() => setIsConfirmationModalOpen(false)}
        onConfirm={leaveSpaceMutation}
        confirmText={isLeaving ? t("removing") : t("remove")}
        disabled={isLeaving}
        confirmVariant="danger"
      >
        {t(`${localeScope}.confirm_remove`, {
          community_member: memberToDelete?.name,
          space: space.name,
        })}
      </ConfirmationModal>
      <BlockDmsModal
        showModal={isBlockDmsModalOpen}
        onClose={() => setIsBlockDmsModalOpen(false)}
        onConfirm={handleBlockDms}
        memberName={memberToBlock?.name}
      />
    </>
  );
};
